import { languageIds } from './highlighting';

// Language detection utilities
export interface LanguageScore {
    language: string;
    score: number;
}

/**
 * Heuristic language detector.
 * Returns one of the supported language ids (or 'plain').
 *
 * NOTE: This is heuristic — for small snippets several languages can share tokens.
 * The function uses targeted strong signals (shebangs, file-ext hints, keywords),
 * structural checks (JSON.parse / XML tags), and tie-break rules to improve accuracy.
 */
export function detectLanguage(code: string): string {
    if (!code || !code.trim()) return 'plain';

    // list of language ids we want to support (ensure coverage)
    const REQUIRED_LANGS = [
        'code', 'cpp', 'csharp', 'go', 'java', 'javascript', 'kotlin', 'python', 'ruby',
        'rust', 'scala', 'shell', 'sql', 'typescript', 'config', 'ini', 'json', 'xml',
        'yaml', 'misc', 'dockerfile', 'markdown', 'proto', 'text', 'log', 'plain',
        'css', 'graphql', 'html', 'php', 'scss', 'tsx', 'jsx', 'tsx', 'ts', 'jsx'
    ];

    const scores: Record<string, number> = {};
    // initialize scores using languageIds when available, but guarantee our required keys exist
    (languageIds || []).forEach(lang => scores[lang] = 0);
    REQUIRED_LANGS.forEach(lang => { if (!(lang in scores)) scores[lang] = 0; });

    const raw = code;
    const sample = code.toLowerCase();
    const lines = raw.split('\n').map(l => l.trim());
    const firstLine = lines[0] || '';

    const add = (lang: string, points: number) => {
        if (!(lang in scores)) scores[lang] = 0;
        scores[lang] += points;
    };

    // ---------- 1) Shebang / file-header shortcuts ----------
    if (firstLine.startsWith('#!')) {
        const s = firstLine.toLowerCase();
        if (s.includes('python')) return 'python';
        if (s.includes('node') || s.includes('nodejs')) return 'javascript';
        if (s.includes('bash') || s.includes('sh')) return 'shell';
        if (s.includes('php')) return 'php';
    }

    // ---------- 2) Strong structural detections (JSON / XML / Markdown / Dockerfile / Proto) ----------
    // JSON: structural - prefer parse if looks like JSON object/array
    const trimmed = sample.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
            // lightweight attempt: JSON.parse is fast for small inputs; guard with try/catch
            JSON.parse(raw);
            add('json', 120); // very strong
        } catch {
            // not strict JSON; still check for many "": " patterns
            const quotePairs = (raw.match(/"\s*:\s*"/g) || []).length + (raw.match(/"\s*:\s*[{[]/g) || []).length;
            if (quotePairs >= 2) add('json', 40);
        }
    }
    // XML
    if (/^\s*<\?xml\b/i.test(raw) || /^\s*<!doctype\s+html>/i.test(raw) || (/<[a-zA-Z][\w:-]*\b[^>]*>/.test(raw) && /<\/[a-zA-Z][\w:-]*>/.test(raw))) {
        add('xml', 80);
        // HTML will be considered separately below
    }
    // Markdown: headings or fenced code blocks
    if (/^\s#{1,6}\s+/m.test(raw) || /```/.test(raw)) add('markdown', 40);
    // Dockerfile: lines starting with FROM, RUN, CMD, COPY at line starts
    if (/^\s*FROM\s+[\w\/:\-\.]+/mi.test(raw) || /^\s*RUN\s+/mi.test(raw)) add('dockerfile', 60);
    // Protobuf
    if (/\bsyntax\s*=\s*"(proto2|proto3)"/.test(sample) || /\bmessage\s+[A-Za-z0-9_]+\s*{/.test(raw)) add('proto', 60);

    // ---------- 3) File extension / filename hints embedded in content ----------
    if (/\b[A-Za-z0-9_\-\/]+\.tsx?\b/.test(raw)) { add('typescript', 40); add('tsx', 40); }
    if (/\b[A-Za-z0-9_\-\/]+\.jsx?\b/.test(raw)) { add('javascript', 30); add('jsx', 30); }
    if (sample.includes('.py') || sample.includes('python')) add('python', 20);
    if (sample.includes('.java')) add('java', 30);
    if (sample.includes('.go')) add('go', 20);
    if (sample.includes('.rs') || sample.includes('rust')) add('rust', 20);
    if (sample.includes('.cpp') || sample.includes('.cc') || sample.includes('.hpp')) add('cpp', 20);
    if (sample.includes('.cs') || sample.includes('csharp')) add('csharp', 20);
    if (sample.includes('.kt') || sample.includes('kotlin')) add('kotlin', 20);
    if (sample.includes('.sql')) add('sql', 15);
    if (sample.includes('.yaml') || sample.includes('.yml')) add('yaml', 30);
    if (sample.includes('.json')) add('json', 20);
    if (sample.includes('dockerfile')) add('dockerfile', 20);
    if (sample.includes('.graphql')) add('graphql', 20);
    if (sample.includes('.xml')) add('xml', 20);

    // ---------- 4) Language-specific keyword heuristics ----------
    // JavaScript / TypeScript / JSX / TSX
    const jsTokens = ['function(', 'function ', '=>', 'console.log', 'window.', 'document.', 'require(', 'module.exports', 'import ', 'export '];
    jsTokens.forEach(tok => { if (sample.includes(tok)) { add('javascript', 6); add('typescript', 2); } });

    // TypeScript stronger signals
    const tsStrong = ['interface ', 'implements ', 'readonly ', 'export type', 'import type', 'declare ', 'namespace ', ': string', ': number', ': boolean', ': any', '\\btype\\b', '\\benum\\b'];
    tsStrong.forEach(tok => { if (new RegExp(tok.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')).test(sample)) add('typescript', 8); });
    if (/\bimport\s+type\b/.test(sample)) add('typescript', 20);
    if (/\bexport\s+interface\b/.test(sample)) add('typescript', 12);
    // generics pattern
    if (/\b[A-Za-z0-9_]+\s*<\s*[A-Za-z0-9_<>,\s?]+\s*>/.test(raw)) { add('typescript', 6); add('cpp', 3); add('java', 2); }

    // JSX/TSX markers (className, onClick, self-closing tags, capitalized component tags)
    const hasJSXClosing = /<\/[A-Z][A-Za-z0-9_:-]*>/.test(raw) || /<\/[a-z][^>]*>/.test(raw);
    const hasJSXAttr = /(className|style=|onClick|onChange|onSubmit|aria-)/.test(raw);
    const hasJSXSelfClosing = /<([A-Za-z][\w:-]*)(\s[^>]*)?\/>/.test(raw);
    if (hasJSXClosing || hasJSXAttr || hasJSXSelfClosing) {
        add('jsx', 30);
        add('tsx', 30);
        add('javascript', 8);
        add('typescript', 8);
    }

    // Java
    const javaTokens = ['public class', 'system.out.println', 'import java.', 'extends ', '@Override', 'implements '];
    javaTokens.forEach(tok => { if (sample.includes(tok)) add('java', 12); });

    // C# (distinctive tokens)
    const csharpTokens = ['using ', 'namespace ', 'console.writeline', '[serializable]', 'public class', 'get; set;'];
    csharpTokens.forEach(tok => { if (sample.includes(tok)) add('csharp', 10); });
    if (/\busing\s+system\b/.test(sample)) add('csharp', 20);

    // C++ / C
    if (/\#\s*include\s*[<"].+[>"]/.test(raw) || /\bstd::\b/.test(sample) || /\bcout\s*<<\b/.test(sample)) add('cpp', 20);

    // Go
    if (/\bpackage\s+[a-z0-9_]+\b/.test(sample) && /\bfunc\s+\w+\s*\(/.test(sample)) add('go', 18);
    if (/:=\s*/.test(sample)) add('go', 6);

    // Kotlin
    if (/\bfun\s+[A-Za-z0-9_]+\s*\(/.test(sample) && /\bval\b|\bvar\b/.test(sample)) add('kotlin', 12);

    // Python
    const pyTokens = ['def ', 'import ', 'from ', 'self', 'print(', 'if __name__'];
    pyTokens.forEach(tok => { if (sample.includes(tok)) add('python', 6); });
    // indentation + colons
    if (/\n\s{4,}\S/.test(raw) && /:\s*(#.*)?\n\s{4,}\S/.test(raw)) add('python', 8);

    // Ruby
    const rubyTokens = ['def ', 'end', 'puts ', 'class ', 'module '];
    rubyTokens.forEach(tok => { if (sample.includes(tok)) add('ruby', 6); });

    // Rust
    if (/\bfn\s+[a-zA-Z_]\w*\s*\(/.test(sample) && sample.includes('let ')) add('rust', 10);
    if (sample.includes('println!(')) add('rust', 10);

    // Scala
    if (/\bobject\b/.test(sample) || /\btrait\b/.test(sample) || /\bextends\b/.test(sample)) add('scala', 10);

    // Shell (besides shebang)
    if (/\becho\b|\bapt-get\b|\byum\b|\bchmod\b|\bchown\b/.test(sample) || /\$\w+/.test(sample)) add('shell', 8);

    // SQL
    if (/\bselect\b|\binsert\b|\bupdate\b|\bdelete\b|\bcreate\s+table\b/.test(sample)) add('sql', 12);

    // YAML (heuristic): many lines with "key: value" and no braces
    const yamlColonLines = (raw.match(/^[\s-]*[A-Za-z0-9_\-]+:\s+/gm) || []).length;
    if (yamlColonLines >= 2 && !/{|}|\[|\]/.test(raw)) add('yaml', 30);

    // INI/config (key=value, [section])
    if (/^\s*\[[^\]]+\]\s*$/m.test(raw) || /^\s*[A-Za-z0-9_.-]+\s*=\s*.+$/m.test(raw)) add('ini', 20);

    // GraphQL
    if (/\btype\s+[A-Z][A-Za-z0-9_]*\s*{/.test(raw) || /\bquery\b|\bmutation\b/.test(sample)) add('graphql', 18);

    // CSS / SCSS
    if (/\.[A-Za-z0-9_\-]+\s*{/.test(raw) || /\#[A-Za-z0-9_\-]+\s*{/.test(raw) || /:[\s]*[a-z-]+\s*;/.test(sample)) add('css', 10);
    if (/@mixin|@include|&\s*\{/.test(raw)) add('scss', 12);

    // PHP
    if (sample.includes('<?php') || /\becho\s+\$[A-Za-z0-9_]+\b/.test(sample) || /\$[A-Za-z0-9_]+\s*=/.test(sample)) add('php', 20);

    // LOG / TEXT / PLAIN signals
    const logLines = raw.split('\n').filter(line => {
        // timestamps (ISO-like), log levels, or common log prefixes
        return /^\s*\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(line) ||
            /^\s*(INFO|WARN|ERROR|DEBUG|TRACE|FATAL|NOTICE)\b/.test(line) ||
            /^\s*\[?(INFO|WARN|ERROR|DEBUG|TRACE|FATAL|NOTICE)\]?/.test(line) ||
            /^\[\d{2}:\d{2}:\d{2}\s*(INFO|WARN|ERROR|DEBUG|TRACE|FATAL|SEVERE)\]/i ||
            /^\[\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?\]\s*\[[^\]]+\/(INFO|WARN|ERROR|DEBUG|TRACE|FATAL|SEVERE)\]/i ||
            /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}\s*(INFO|WARN|ERROR|DEBUG|TRACE|FATAL|SEVERE)/i ||
            /^(INFO|WARN|ERROR|DEBUG|TRACE|FATAL|SEVERE):/i ||
            /\b\w+=("[^"]*"|\S+)/
            ;
    });

    if (logLines.length >= 1) {
        // small snippets: weak boost, longer: stronger
        add('log', Math.min(10 + logLines.length * 3, 40));
    }

    // Text / misc fallback (very weak, only if no strong language detected)
    if (raw.length < 200 && Object.values(scores).every(s => s < 5)) {
        add('text', 2);
        add('log', 2);
    }

    // ---------- 5) Heuristics / conflict resolution ----------
    // Avoid HTML false positives: only keep HTML if its score is strong AND not overwhelmed by JS/TS tokens
    const htmlTagCount = (raw.match(/<\/?[a-zA-Z][\w:-]*\b[^>]*>/g) || []).length;
    if (htmlTagCount >= 2 && /<\s*html\b|<!doctype/i.test(raw)) {
        add('html', 60);
    } else if (htmlTagCount >= 3 && !/\b(import |export |function|=>|interface )/.test(sample)) {
        add('html', 30);
    }

    // If JSON was strongly detected, reduce chance of other false positives
    if ((scores.json || 0) >= 80) {
        // make json winning by removing some points from ambiguous languages
        add('csharp', -10);
        add('java', -10);
        add('yaml', -5);
    }

    // If JSX/TSX markers present, reduce HTML score (avoid classifying TSX as HTML)
    if (hasJSXAttr || hasJSXSelfClosing || /<\/[A-Z]/.test(raw)) {
        add('html', -40);
        add('jsx', 20);
        add('tsx', 20);
    }

    // If many JS tokens exist, bias toward javascript/typescript
    const jsTokenCount = (sample.match(/\b(function|const|let|var|=>|import|export|class|interface)\b/g) || []).length;
    if (jsTokenCount >= 3) {
        add('javascript', jsTokenCount * 3);
        // boost typescript if ts tokens present
        const tsTokenCount = (sample.match(/\b(interface|implements|readonly|import type|export type|declare)\b/g) || []).length;
        if (tsTokenCount > 0) add('typescript', tsTokenCount * 6);
    }

    // csharp vs java tie-breaker: if 'using system' or 'console.writeline' prefer csharp
    if (/\busing\s+system\b/.test(sample) || /\bconsole\.writeline\b/.test(sample)) add('csharp', 20);

    // Java strong tokens get strong weight to avoid plain fallback
    if (/\bpublic\s+class\b/.test(sample) || /\bsystem\.out\.println\b/.test(sample) || /\bpackage\s+[a-z0-9_.]+\b/.test(sample)) add('java', 18);

    // ---------- 6) Final selection ----------
    let best = 'plain';
    let bestScore = 0;
    for (const [lang, sc] of Object.entries(scores)) {
        if (sc > bestScore) {
            bestScore = sc;
            best = lang;
        } else if (sc === bestScore && sc > 0) {
            // tie-break preferences
            if (best === 'javascript' && lang === 'typescript') best = 'typescript';
            if (best === 'javascript' && lang === 'jsx') best = 'jsx';
            if (best === 'typescript' && lang === 'tsx') best = 'tsx';
        }
    }

    // Minimum confidence required
    const MIN_CONF = 12;
    return bestScore >= MIN_CONF ? best : 'plain';
}
